import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { listenToListingAvailability } from "../../../redux/shortstayReservationSlice";
import { RootState, AppDispatch } from "../../../redux/store";
import { listenToShortstayData } from "../../../redux/shortstaysSlice";
import dayjs from "dayjs";
import CircularProgress from "@mui/material/CircularProgress";
import { calculateShortstayEarnings } from "../../../redux/shortstayServiceFeeSlice";
import { useCurrency } from "@cosmoola/react-cosmoola-js";
import countries, { CountryInfo } from "../../../GLOBAL/Countries";

import {
  Lock, Smartphone, Check, ChevronDown, ChevronUp, ChevronRight,
  Users, Mail, AlertCircle, X, ArrowLeft, ShieldCheck,
  CreditCard, RotateCcw, Home as HomeIcon,
} from "lucide-react";

/* ═══════════════════════════════════════════════
   ENDPOINTS  (same pattern as Armerix)
═══════════════════════════════════════════════ */
const STRIPE_CHECKOUT_URL    = "https://europe-west3-alreluxe.cloudfunctions.net/createCheckoutSession";
const COSMOOLA_CHECKOUT_URL  = "https://europe-west3-alreluxe.cloudfunctions.net/createCosmoolaCheckoutSession";
const CREATE_CUSTOMER_URL    = "https://europe-west3-alreluxe.cloudfunctions.net/createCustomerAccount";

type PaymentMethod = "stripe" | "mpesa";
type CheckoutResponse = { id?: string; url?: string; data?: { url?: string }; sessionUrl?: string; checkoutUrl?: string };
type CreateCustomerResponse = { userId: string };

/* ═══════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════ */
const T = {
  bg:        "#FFFFFF",
  bgSoft:    "#F8F5F0",
  bgMuted:   "#EDE8E0",
  ink:       "#0D0D0D",
  muted:     "#8A7A6A",
  mutedSoft: "#C4B8A8",
  border:    "rgba(184,134,11,0.18)",
  gold:      "#B8860B",
  red:       "#CC2200",
  orange:    "#FF6B00",
  green:     "#15803D",
  grad:      "linear-gradient(135deg,#0D0D0D 0%,#B8860B 35%,#CC2200 65%,#FF6B00 100%)",
  gradH:     "linear-gradient(90deg,#0D0D0D 0%,#B8860B 25%,#CC2200 60%,#FF6B00 100%)",
};

/* ═══════════════════════════════════════════════
   CSS
═══════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&family=Cormorant+Garamond:wght@400;600;700&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  button,input,select,textarea{font-family:inherit;cursor:pointer;}
  ::-webkit-scrollbar{display:none;}

  @keyframes gradSpin{0%{background-position:0% 50%}50%{background-position:100% 50%}100%{background-position:0% 50%}}
  @keyframes spin{to{transform:rotate(360deg)}}
  @keyframes slideUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  @keyframes mpesaPulse{0%,100%{box-shadow:0 0 0 0 rgba(0,166,81,.35)}50%{box-shadow:0 0 0 6px rgba(0,166,81,0)}}

  .fi{width:100%;padding:10px 12px;border:1px solid #EDE8E0;background:#fff;font-family:inherit;font-size:13px;color:#0D0D0D;outline:none;transition:border-color .15s;border-radius:2px;}
  .fi:focus{border-color:#B8860B;}
  .fi::placeholder{color:#C4B8A8;}
  .fl{display:block;font-size:10px;font-weight:700;color:#8A7A6A;letter-spacing:.12em;text-transform:uppercase;margin-bottom:5px;}

  .pay-method-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:14px;}
  .pay-method-card{border:2px solid #EDE8E0;background:#fff;border-radius:8px;padding:16px 12px;cursor:pointer;display:flex;flex-direction:column;align-items:center;gap:7px;transition:all .2s;position:relative;overflow:hidden;user-select:none;}
  .pay-method-card:hover{border-color:#B8860B;background:#FAFAF8;transform:translateY(-2px);}
  .pay-method-card.stripe.selected{border-color:#0D0D0D;background:#FAFAF8;box-shadow:0 6px 20px rgba(0,0,0,.14);transform:translateY(-2px);}
  .pay-method-card.mpesa.selected{border-color:#00A651;background:rgba(0,166,81,.04);box-shadow:0 6px 20px rgba(0,166,81,.2);transform:translateY(-2px);}
  .pay-method-top-bar{position:absolute;top:0;left:0;right:0;height:3px;opacity:0;transition:opacity .18s;}
  .pay-method-card.selected .pay-method-top-bar{opacity:1;}
  .pay-method-check{position:absolute;top:8px;right:8px;width:16px;height:16px;border-radius:50%;border:1.5px solid #EDE8E0;background:#fff;display:flex;align-items:center;justify-content:center;transition:all .15s;}
  .pay-method-card.stripe.selected .pay-method-check{background:#0D0D0D;border-color:#0D0D0D;}
  .pay-method-card.mpesa.selected  .pay-method-check{background:#00A651;border-color:#00A651;}
  .pay-method-label{font-size:10px;font-weight:800;letter-spacing:.1em;text-transform:uppercase;color:#0D0D0D;text-align:center;}
  .pay-method-sub{font-size:9px;color:#8A7A6A;text-align:center;line-height:1.4;}

  .pay-btn-stripe{width:100%;padding:14px 0;border:none;border-radius:4px;font-size:13px;font-weight:700;letter-spacing:.04em;font-family:inherit;display:flex;align-items:center;justify-content:center;gap:8px;transition:all .2s;}
  .pay-btn-stripe.active{background:#0D0D0D;color:#fff;cursor:pointer;box-shadow:0 4px 16px rgba(0,0,0,.18);}
  .pay-btn-stripe.active:hover{background:#1A1A1A;box-shadow:0 6px 22px rgba(0,0,0,.25);}
  .pay-btn-stripe.inactive{background:#C4B8A8;color:#fff;cursor:not-allowed;}

  .pay-btn-mpesa{width:100%;padding:14px 0;border:none;border-radius:4px;font-size:13px;font-weight:700;letter-spacing:.04em;font-family:inherit;display:flex;align-items:center;justify-content:center;gap:8px;transition:all .2s;}
  .pay-btn-mpesa.active{background:#00A651;color:#fff;cursor:pointer;box-shadow:0 4px 16px rgba(0,166,81,.25);animation:mpesaPulse 2.5s ease infinite;}
  .pay-btn-mpesa.active:hover{background:#009348;}
  .pay-btn-mpesa.inactive{background:rgba(0,166,81,.12);color:rgba(0,166,81,.4);cursor:not-allowed;}

  .exp-card{border:1px solid #EDE8E0;border-radius:8px;overflow:hidden;margin-bottom:10px;background:#fff;}
  .exp-card-header{display:flex;align-items:center;gap:12px;padding:13px 16px;cursor:pointer;user-select:none;transition:background .13s;}
  .exp-card-header:hover{background:#FAFAF8;}

  .bfs-toggle{display:flex;align-items:center;gap:10px;padding:11px 13px;border:1px dashed #C4B8A8;cursor:pointer;transition:all .15s;background:transparent;width:100%;border-radius:3px;text-align:left;}
  .bfs-toggle:hover{border-color:#0D0D0D;}
  .bfs-toggle.on{border-style:solid;border-color:#0D0D0D;background:#FAFAF8;}
  .shein-check{width:18px;height:18px;border-radius:2px;border:1.5px solid #B8860B;background:#fff;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:all .15s;}
  .shein-check.on{background:#0D0D0D;border-color:#0D0D0D;}

  .country-dropdown{position:absolute;top:100%;left:0;right:0;z-index:500;background:#fff;border:1px solid #B8860B;border-top:none;max-height:220px;overflow-y:auto;box-shadow:0 8px 24px rgba(0,0,0,.12);}
  .country-option{display:flex;align-items:center;gap:10px;padding:9px 12px;cursor:pointer;font-size:12px;color:#0D0D0D;transition:background .12s;border-bottom:1px solid #F0EBE3;}
  .country-option:hover{background:#FAFAF8;}
  .dial-dropdown{position:absolute;top:100%;left:0;z-index:500;background:#fff;border:1px solid #B8860B;border-top:none;width:220px;max-height:220px;overflow-y:auto;box-shadow:0 8px 24px rgba(0,0,0,.12);}
  .dial-option{display:flex;align-items:center;gap:10px;padding:9px 12px;cursor:pointer;font-size:12px;color:#0D0D0D;transition:background .12s;border-bottom:1px solid #F0EBE3;}
  .dial-option:hover{background:#FAFAF8;}
`;

/* ═══════════════════════════════════════════════
   TINY HELPERS
═══════════════════════════════════════════════ */
function flagSphere(emoji: string) {
  return (
    <div style={{ position: "relative", width: 22, height: 22, flexShrink: 0 }}>
      <div style={{ position: "absolute", inset: 0, borderRadius: "50%", background: "radial-gradient(circle at 35% 32%,rgba(255,255,255,.55) 0%,rgba(255,255,255,.10) 28%,transparent 52%),radial-gradient(circle at 68% 72%,rgba(0,0,0,.18) 0%,transparent 55%)", zIndex: 1, pointerEvents: "none" }} />
      <div style={{ width: 22, height: 22, borderRadius: "50%", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, lineHeight: 1, background: T.bgMuted }}>{emoji}</div>
    </div>
  );
}

function stripDialCode(raw: string, dialCode: string): string {
  const t = raw.trim();
  if (t.startsWith(dialCode)) return t.slice(dialCode.length).trim();
  for (const c of countries) { if (t.startsWith(c.phoneCode)) return t.slice(c.phoneCode.length).trim(); }
  return t.replace(/^\+/, "");
}

const FeeRow: React.FC<{ label: string; value: string; muted?: boolean; green?: boolean; bold?: boolean }> = ({ label, value, muted, green, bold }) => (
  <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px dashed rgba(0,0,0,.07)", fontSize: bold ? 14 : 13, color: muted ? T.muted : T.ink, fontWeight: bold ? 800 : 400 }}>
    <span>{label}</span>
    <span style={{ color: green ? T.green : undefined, fontWeight: bold ? 800 : 600 }}>{value}</span>
  </div>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div><label className="fl">{label}</label>{children}</div>
);

/* ═══════════════════════════════════════════════
   COUNTRY / DIAL PICKERS  (same as Armerix)
═══════════════════════════════════════════════ */
const CountryField: React.FC<{ value: string; onChange: (c: CountryInfo) => void }> = React.memo(({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = React.useRef<HTMLDivElement>(null);
  const selected = countries.find(c => c.name === value);
  const filtered = React.useMemo(() => { const q = query.toLowerCase(); return q ? countries.filter(c => c.name.toLowerCase().includes(q)) : countries; }, [query]);
  useEffect(() => { const fn = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }; document.addEventListener("mousedown", fn); return () => document.removeEventListener("mousedown", fn); }, []);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div onMouseDown={e => { e.preventDefault(); setOpen(o => !o); setQuery(""); }}
        style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 12px", border: `1px solid ${open ? T.gold : T.border}`, background: "#fff", cursor: "pointer", borderRadius: 2 }}>
        {selected ? <>{flagSphere(selected.emoji)}<span style={{ fontSize: 13, color: T.ink, flex: 1 }}>{selected.name}</span></> : <span style={{ fontSize: 13, color: T.mutedSoft, flex: 1 }}>Select country</span>}
        <ChevronDown size={13} color={T.muted} style={{ transition: "transform .15s", transform: open ? "rotate(180deg)" : "none" }} />
      </div>
      <AnimatePresence>
        {open && (
          <motion.div className="country-dropdown" onMouseDown={e => e.preventDefault()} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: .14 }}>
            <div style={{ padding: "8px 10px", borderBottom: "1px solid #F0EBE3", position: "sticky", top: 0, background: "#fff" }}>
              <input autoFocus placeholder="Search country…" value={query} onChange={e => setQuery(e.target.value)} style={{ width: "100%", padding: "7px 10px", border: "1px solid #EDE8E0", borderRadius: 2, fontFamily: "inherit", fontSize: 12, outline: "none" }} />
            </div>
            {filtered.slice(0, 80).map(c => (
              <div key={c.code} className="country-option" onClick={() => { onChange(c); setOpen(false); }}>
                {flagSphere(c.emoji)}<span style={{ flex: 1 }}>{c.name}</span><span style={{ color: T.muted, fontSize: 11 }}>{c.phoneCode}</span>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

const DialField: React.FC<{ dialCode: string; onChange: (code: string) => void }> = React.memo(({ dialCode, onChange }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = React.useRef<HTMLDivElement>(null);
  const selected = countries.find(c => c.phoneCode === dialCode);
  const filtered = React.useMemo(() => { const q = query.toLowerCase(); return q ? countries.filter(c => c.name.toLowerCase().includes(q) || c.phoneCode.includes(q)) : countries; }, [query]);
  useEffect(() => { const fn = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }; document.addEventListener("mousedown", fn); return () => document.removeEventListener("mousedown", fn); }, []);
  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      <div onMouseDown={e => { e.preventDefault(); setOpen(o => !o); setQuery(""); }}
        style={{ display: "flex", alignItems: "center", gap: 7, padding: "10px 10px", border: "1px solid #EDE8E0", borderRight: "none", background: T.bgSoft, cursor: "pointer", minWidth: 90, borderRadius: "2px 0 0 2px" }}>
        {selected && flagSphere(selected.emoji)}
        <span style={{ fontSize: 12, color: T.ink }}>{dialCode}</span>
        <ChevronDown size={11} color={T.muted} />
      </div>
      <AnimatePresence>
        {open && (
          <motion.div className="dial-dropdown" onMouseDown={e => e.preventDefault()} initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: .14 }}>
            <div style={{ padding: "7px 8px", borderBottom: "1px solid #F0EBE3", position: "sticky", top: 0, background: "#fff" }}>
              <input autoFocus placeholder="Search…" value={query} onChange={e => setQuery(e.target.value)} style={{ width: "100%", padding: "6px 8px", border: "1px solid #EDE8E0", borderRadius: 2, fontFamily: "inherit", fontSize: 11, outline: "none" }} />
            </div>
            {filtered.slice(0, 80).map(c => (
              <div key={c.code} className="dial-option" onClick={() => { onChange(c.phoneCode); setOpen(false); }}>
                {flagSphere(c.emoji)}<span style={{ flex: 1, fontSize: 11 }}>{c.name}</span><span style={{ color: T.muted, fontSize: 11, fontWeight: 700 }}>{c.phoneCode}</span>
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

/* ═══════════════════════════════════════════════
   EXPANDABLE CARD  (mobile accordion / desktop open)
═══════════════════════════════════════════════ */
const ExpandCard: React.FC<{
  title: string; icon?: React.ReactNode;
  badge?: React.ReactNode; defaultOpen?: boolean;
  forceOpen?: boolean; children: React.ReactNode;
}> = ({ title, icon, badge, defaultOpen = false, forceOpen, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = forceOpen !== undefined ? forceOpen : open;
  return (
    <div className="exp-card">
      <div className="exp-card-header" onClick={() => forceOpen === undefined && setOpen(v => !v)}>
        {icon && <span style={{ display: "flex", alignItems: "center", color: T.gold }}>{icon}</span>}
        <span style={{ fontSize: 12, fontWeight: 800, letterSpacing: ".12em", textTransform: "uppercase" as const, color: T.ink, flex: 1 }}>{title}</span>
        {badge}
        {forceOpen === undefined && (isOpen
          ? <ChevronUp size={15} color={T.muted} />
          : <ChevronDown size={15} color={T.muted} />)}
      </div>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: .25, ease: [.4, 0, .2, 1] }}
            style={{ overflow: "hidden" }}>
            <div style={{ padding: "14px 16px", borderTop: "1px solid #EDE8E0" }}>
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ═══════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════ */
export default function ShortAccomodationPreview() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const dispatch  = useDispatch<AppDispatch>();
  const { listingId } = useParams<{ listingId: string }>();

  const { user }            = useSelector((s: RootState) => s.auth);
  const { currentListing }  = useSelector((s: RootState) => s.shortstays);
  const listingAvailability = useSelector((s: RootState) => s.shortstayReservations.listingAvailability);
  const { available, conflict } = listingAvailability || { available: true, conflict: null };
  const {
    baseStayCost, discountedStayCost, guestsFee,
    totalCharged, creditsUsed, fullCost,
  } = useSelector((s: RootState) => s.shortstayServiceFees);

  const { convertRaw, symbol } = useCurrency();

  /* ── query params ── */
  const q                = new URLSearchParams(location.search);
  const checkIn          = q.get("checkIn") || "";
  const checkOut         = q.get("checkOut") || "";
  const selectedExtras   = JSON.parse(q.get("selectedExtras") || "[]");
  const reservedDuration = Number(q.get("reservedDuration") || 0);
  const adults           = Number(q.get("adults") || 1);
  const children         = Number(q.get("children") || 0);
  const infants          = Number(q.get("infants") || 0);
  const pets             = Number(q.get("pets") || 0);
  const discountApplied  = JSON.parse(q.get("discount") || "{}");
  const creditsAvailable = Number(q.get("credits") || 0);
  const totalHours       = Number(q.get("totalHours") || 0);
  const selectedTimes    = JSON.parse(q.get("selectedTimes") || "[]");

  const isPerHour  = currentListing?.rateType === "perHour" || currentListing?.rateType === "perHourPerPerson";
  const isPerWeek  = currentListing?.rateType === "perWeek";
  const isPerMonth = currentListing?.rateType === "perMonth";
  const isPerYear  = currentListing?.rateType === "perYear";

  /* ── responsive ── */
  const [isDesktop, setIsDesktop] = useState(typeof window !== "undefined" && window.innerWidth >= 900);
  useEffect(() => {
    const r = () => setIsDesktop(window.innerWidth >= 900);
    window.addEventListener("resize", r);
    return () => window.removeEventListener("resize", r);
  }, []);

  /* ── contact form state ── */
  type AddrState = { firstName: string; lastName: string; email: string; country: string; dialCode: string; phone: string; };
  const [addr, setAddr] = useState<AddrState>({ firstName: "", lastName: "", email: "", country: "", dialCode: "+254", phone: "" });
  const patchAddr = useCallback((patch: Partial<AddrState>) => setAddr(p => ({ ...p, ...patch })), []);

  /* prefill from auth user */
  useEffect(() => {
    if (!user) return;
    const rawPhone  = user.contactNumber || user.whatsappNumber || "";
    const dialMatch = rawPhone.match(/^(\+\d{1,4})\s?/);
    const dialCode  = dialMatch ? dialMatch[1] : "+254";
    const phone     = dialMatch ? rawPhone.replace(dialMatch[0], "") : rawPhone;
    setAddr({
      firstName: user.firstName || "",
      lastName:  user.lastName  || (user as any).surName || "",
      email:     user.contactEmail || user.email || user.emailAddress || "",
      country:   user.shippingAddress?.country || "",
      dialCode,
      phone,
    });
  }, [user]);

  const rFirstName = useRef<HTMLInputElement>(null);
  const rLastName  = useRef<HTMLInputElement>(null);
  const rEmail     = useRef<HTMLInputElement>(null);
  const rPhone     = useRef<HTMLInputElement>(null);

  /* ── book for someone else ── */
  const [bookForSomeoneElse, setBookForSomeoneElse] = useState(false);
  const [recipientFirstName, setRecipientFirstName] = useState("");
  const [recipientLastName,  setRecipientLastName]  = useState("");
  const [recipientEmail,     setRecipientEmail]     = useState("");
  const [recipientDialCode,  setRecipientDialCode]  = useState("+254");
  const [recipientPhone,     setRecipientPhone]     = useState("");

  /* ── payment ── */
  const [selectedPayment, setSelectedPayment] = useState<PaymentMethod>("stripe");
  const [payingStripe,    setPayingStripe]    = useState(false);
  const [payingMpesa,     setPayingMpesa]     = useState(false);
  const [payError,        setPayError]        = useState<string | null>(null);
  const paying = payingStripe || payingMpesa;

  const [isAfrica, setIsAfrica] = useState(false);
  const AFRICAN_COUNTRIES = new Set([
    "nigeria","kenya","south africa","egypt","morocco","ghana","tanzania","ethiopia",
    "uganda","rwanda","senegal","ivory coast","cameroon","zambia","zimbabwe","botswana",
    "namibia","mozambique","algeria","tunisia","libya","sudan","angola",
    "mali","niger","chad","somalia","malawi","benin","togo","sierra leone",
    "liberia","guinea","burkina faso","gabon","mauritius","eswatini","lesotho",
    "gambia","madagascar",
  ]);

  useEffect(() => {
    if (!navigator.geolocation || !window.google?.maps) return;
    navigator.geolocation.getCurrentPosition((pos) => {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode(
        { location: { lat: pos.coords.latitude, lng: pos.coords.longitude } },
        (results: google.maps.GeocoderResult[] | null, status: google.maps.GeocoderStatus) => {
          if (status === "OK" && results?.[0]) {
            const country = results[0].address_components.find(
              (c: google.maps.GeocoderAddressComponent) => c.types.includes("country")
            )?.long_name;
            if (country && AFRICAN_COUNTRIES.has(country.toLowerCase())) setIsAfrica(true);
          }
        }
      );
    });
  }, []);

  useEffect(() => {
    if (!isAfrica && selectedPayment === "mpesa") setSelectedPayment("stripe");
  }, [isAfrica, selectedPayment]);

  /* ── data loading ── */
  useEffect(() => {
    if (!listingId || !checkIn || !checkOut) return;
    const unsub = dispatch(listenToListingAvailability(listingId, checkIn, checkOut));
    return () => { if (unsub) unsub(); };
  }, [dispatch, listingId, checkIn, checkOut]);

  useEffect(() => {
    if (!listingId || (currentListing && currentListing.listingId === listingId)) return;
    dispatch(listenToShortstayData(listingId));
  }, [dispatch, listingId, currentListing]);

  const allExtras = currentListing?.extras || {};
  const selectedExtrasMap = selectedExtras.reduce((acc: any, name: string) => {
    if (allExtras[name]) acc[name] = allExtras[name];
    return acc;
  }, {});

  useEffect(() => {
    if (!currentListing?.rate || !currentListing.rateType) return;
    if (!isPerHour && !reservedDuration) return;
    if (isPerHour && totalHours === 0) return;
    const extrasTotal = Object.values(selectedExtrasMap).reduce((s: number, v: any) => s + Number(v), 0);
    dispatch(calculateShortstayEarnings({
      rate: currentListing.rate, rateType: currentListing.rateType,
      reservedDuration, totalHours, adults,
      extraCharges: extrasTotal, discountApplied, creditsAvailable,
    }));
  }, [dispatch, currentListing, reservedDuration, totalHours, adults, discountApplied, creditsAvailable, isPerHour, selectedExtrasMap]);

  /* ── customer account helper (same as Armerix) ── */
  const ensureCustomerAccount = async (): Promise<string> => {
    const firstName = bookForSomeoneElse ? recipientFirstName : (rFirstName.current?.value || addr.firstName);
    const lastName  = bookForSomeoneElse ? recipientLastName  : (rLastName.current?.value  || addr.lastName);
    const email     = bookForSomeoneElse ? recipientEmail     : (rEmail.current?.value     || addr.email);
    const phone     = bookForSomeoneElse
      ? `${recipientDialCode}${stripDialCode(recipientPhone, recipientDialCode)}`
      : `${addr.dialCode}${stripDialCode(rPhone.current?.value || addr.phone, addr.dialCode)}`;

    if (user && !bookForSomeoneElse) return user.userId;

    const res = await axios.post<CreateCustomerResponse>(CREATE_CUSTOMER_URL, {
      firstName, lastName, email, phone,
      bookForSomeoneElse,
      buyerUserId: user?.userId ?? null,
    });
    if (!res.data?.userId) throw new Error("Account creation failed — no userId returned.");
    return res.data.userId;
  };

  /* ── shared payload builder ── */
  const buildPayload = useCallback(async () => {
    const resolvedUserId = await ensureCustomerAccount();
    return {
      resolvedUserId,
      body: {
        listingId,
        userId:            resolvedUserId,
        buyerUserId:       user?.userId ?? null,
        checkIn, checkOut,
        reservedDuration, totalHours, selectedTimes, selectedExtras,
        adults, children, infants, pets,
        discountApplied, creditsAvailable,
        bookForSomeoneElse,
        contactFirstName:  rFirstName.current?.value || addr.firstName,
        contactLastName:   rLastName.current?.value  || addr.lastName,
        contactEmail:      rEmail.current?.value     || addr.email,
        contactPhone:      `${addr.dialCode}${stripDialCode(rPhone.current?.value || addr.phone, addr.dialCode)}`,
      },
    };
  }, [listingId, user, addr, bookForSomeoneElse, recipientFirstName, recipientLastName, recipientEmail, recipientDialCode, recipientPhone,
      checkIn, checkOut, reservedDuration, totalHours, selectedTimes, selectedExtras,
      adults, children, infants, pets, discountApplied, creditsAvailable]);

  const redirectFromResponse = (data: CheckoutResponse) => {
    const directUrl = data?.url ?? data?.data?.url ?? data?.sessionUrl ?? data?.checkoutUrl;
    if (directUrl)  { window.location.href = directUrl; return; }
    if (data?.id)   { window.location.href = `https://checkout.stripe.com/c/pay/${data.id}`; return; }
    throw new Error(`No checkout URL returned. Server: ${JSON.stringify(data)}`);
  };

  const handlePayStripe = useCallback(async () => {
    if (paying) return;
    setPayingStripe(true); setPayError(null);
    try {
      const { body } = await buildPayload();
      const res = await axios.post<CheckoutResponse>(STRIPE_CHECKOUT_URL, body);
      redirectFromResponse(res.data);
    } catch (e: any) {
      setPayError(e?.message ?? "Stripe payment failed. Please try again.");
      setPayingStripe(false);
    }
  }, [paying, buildPayload]);

  const handlePayMpesa = useCallback(async () => {
    if (paying) return;
    setPayingMpesa(true); setPayError(null);
    try {
      const { body } = await buildPayload();
      const res = await axios.post<CheckoutResponse>(COSMOOLA_CHECKOUT_URL, body);
      const url = res.data?.url ?? res.data?.data?.url ?? res.data?.sessionUrl ?? res.data?.checkoutUrl;
      if (!url) throw new Error(`No Cosmoola URL returned. Server: ${JSON.stringify(res.data)}`);
      window.location.href = url;
    } catch (e: any) {
      setPayError(e?.message ?? "M-Pesa payment failed. Please try again.");
      setPayingMpesa(false);
    }
  }, [paying, buildPayload]);

  const prettyAmount = convertRaw(totalCharged, "EUR").toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const prettyCheckIn  = dayjs(checkIn).format("DD MMM YYYY");
  const prettyCheckOut = dayjs(checkOut).format("DD MMM YYYY");

  const durationLabel = () => {
    if (isPerHour)  return `${totalHours} hour${totalHours > 1 ? "s" : ""}`;
    if (isPerWeek)  { const w = Math.ceil(reservedDuration / 7);  return `${w} week${w > 1 ? "s" : ""}`; }
    if (isPerMonth) { const m = Math.ceil(reservedDuration / 30); return `${m} month${m > 1 ? "s" : ""}`; }
    if (isPerYear)  { const y = Math.ceil(reservedDuration / 365); return `${y} year${y > 1 ? "s" : ""}`; }
    return prettyCheckOut;
  };

  const guestLabel = `${adults} adult${adults > 1 ? "s" : ""}${children ? `, ${children} child${children > 1 ? "ren" : ""}` : ""}${infants ? `, ${infants} infant${infants > 1 ? "s" : ""}` : ""}${pets ? `, ${pets} pet${pets > 1 ? "s" : ""}` : ""}`;

  /* ─── LOADING ─── */
  if (!currentListing) {
    return (
      <div style={{ background: "#000", color: "#fff", display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", gap: 16, flexDirection: "column" }}>
        <CircularProgress size={36} thickness={4} style={{ color: "#fff" }} />
        <p style={{ fontWeight: 700 }}>Loading your reservation preview…</p>
      </div>
    );
  }

  /* ─── UNAVAILABLE ─── */
  if (!available && conflict) {
    return (
      <div style={{ position: "fixed", inset: 0, background: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 24, textAlign: "center" }}>
        <h2>These dates are no longer available</h2>
        <p style={{ color: "#666", margin: "12px 0 24px" }}>Another guest has just booked this stay. Please select new dates to continue.</p>
        <button onClick={() => navigate(`/roomdetails/${listingId}`)}
          style={{ padding: "12px 20px", background: "#000", color: "#fff", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>
          Back to calendar
        </button>
      </div>
    );
  }

  /* ════════════════════════════════════════════
     SHARED SECTION COMPONENTS
  ════════════════════════════════════════════ */

  /* ── Listing summary ── */
  const ListingSummaryContent = () => (
    <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
      <div onClick={() => navigate(`/roomdetails/${listingId}`)}
        style={{ width: 72, height: 72, borderRadius: 10, overflow: "hidden", flexShrink: 0, cursor: "pointer", boxShadow: "0 4px 12px rgba(0,0,0,.12)" }}>
        <img src={currentListing.photos?.[0]} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      </div>
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: 15, fontWeight: 900, color: T.ink, lineHeight: 1.25, marginBottom: 4, fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
          {currentListing.title || currentListing.name}
        </p>
        <p style={{ fontSize: 12, color: T.muted, lineHeight: 1.4 }}>
          {currentListing.address?.street}, {currentListing.address?.city || currentListing.address?.county}
          {currentListing.address?.country ? `, ${currentListing.address.country}` : ""}
        </p>
      </div>
    </div>
  );

  /* ── Stay summary ── */
  const StaySummaryContent = () => (
    <div>
<div style={{ display: "grid", gridTemplateColumns: isPerHour ? "1fr 1fr" : "1fr 1fr 1fr", gap: 0, border: "1px solid #EDE8E0", borderRadius: 6, overflow: "hidden", marginBottom: 10 }}>
        <div style={{ padding: "12px 14px" }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".08em", color: T.muted, marginBottom: 3 }}>CHECK-IN</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{prettyCheckIn || "—"}</div>
        </div>

        {!isPerHour && (
          <div style={{ padding: "12px 14px", borderLeft: "1px solid #EDE8E0" }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".08em", color: T.muted, marginBottom: 3 }}>CHECK-OUT</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{prettyCheckOut || "—"}</div>
          </div>
        )}

       <div style={{ padding: "12px 14px", borderLeft: "1px solid #EDE8E0" }}>
  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".08em", color: T.muted, marginBottom: 3 }}>
    {isPerHour ? "DURATION" : "NIGHTS"}
  </div>
  <div style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>
    {isPerHour
      ? durationLabel()
      : isPerWeek || isPerMonth || isPerYear
      ? durationLabel()
      : (() => {
          if (!checkIn || !checkOut) return "—";
          const n = dayjs(checkOut).diff(dayjs(checkIn), "day");
          return `${n} night${n !== 1 ? "s" : ""}`;
        })()
    }
  </div>
</div>

        <div style={{ padding: "10px 14px", borderTop: "1px solid #EDE8E0", gridColumn: "1 / -1" }}>
          <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".08em", color: T.muted, marginBottom: 3 }}>GUESTS</div>
          <div style={{ fontSize: 13, fontWeight: 600, color: T.ink }}>{guestLabel}</div>
        </div>
      </div>

      
      {isPerHour && totalHours > 0 && selectedTimes.length > 0 && (
        <div style={{ fontSize: 12, color: T.muted, background: T.bgSoft, padding: "8px 12px", borderRadius: 4, border: `1px solid ${T.border}` }}>
          {dayjs(`${checkIn} ${selectedTimes[0]}`).format("h A")} – {dayjs(`${checkIn} ${selectedTimes[0]}`).add(totalHours, "hour").format("h A")}
        </div>
      )}
    </div>
  );

  /* ── Contact fields ── */
  const ContactContent = () => (
    <div>
      {user && !bookForSomeoneElse && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: "rgba(21,128,61,.06)", border: "1px solid rgba(21,128,61,.2)", padding: "9px 12px", marginBottom: 14, borderRadius: 3 }}>
          <Check size={12} color={T.green} />
          <p style={{ fontSize: 11, color: T.green }}>Pre-filled from your profile — all fields are still editable.</p>
        </div>
      )}
      <div style={{ display: "none" }}>
        <Field label="First Name *">
          <input ref={rFirstName} className="fi" placeholder="e.g. Alex" defaultValue={addr.firstName} onBlur={e => patchAddr({ firstName: e.target.value })} />
        </Field>
        <Field label="Last Name *">
          <input ref={rLastName} className="fi" placeholder="e.g. Smith" defaultValue={addr.lastName} onBlur={e => patchAddr({ lastName: e.target.value })} />
        </Field>
      </div>
      <div style={{ display: "none" }}>
        <Field label="Email Address *">
          <div style={{ position: "relative" }}>
            <input ref={rEmail} className="fi" placeholder="your@email.com" defaultValue={addr.email} onBlur={e => patchAddr({ email: e.target.value })} style={{ paddingLeft: 38 }} />
            <Mail size={13} color={T.muted} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
          </div>
        </Field>
      </div>
      <div style={{ display: "none" }}>
        <Field label="Country *">
          <CountryField value={addr.country} onChange={c => patchAddr({ country: c.name, dialCode: c.phoneCode })} />
        </Field>
      </div>
      <div style={{ display: "none" }}>
        <Field label="WhatsApp Number *">
          <div style={{ display: "flex" }}>
            <DialField dialCode={addr.dialCode} onChange={dc => patchAddr({ dialCode: dc })} />
            <input ref={rPhone} className="fi" placeholder="e.g. 7001234567" defaultValue={addr.phone}
              onBlur={e => { const clean = stripDialCode(e.target.value, addr.dialCode); patchAddr({ phone: clean }); if (e.target.value !== clean) e.target.value = clean; }}
              style={{ flex: 1, borderRadius: "0 2px 2px 0" }} inputMode="tel" />
          </div>
          <p style={{ fontSize: 10, color: T.muted, marginTop: 4 }}>{addr.dialCode} + your number · dial code added automatically</p>
        </Field>
      </div>

      {/* Book for someone else */}
      <button className={`bfs-toggle${bookForSomeoneElse ? " on" : ""}`} onClick={() => setBookForSomeoneElse(v => !v)}>
        <div className={`shein-check${bookForSomeoneElse ? " on" : ""}`}>{bookForSomeoneElse && <Check size={10} color="#fff" />}</div>
        <Users size={13} color={bookForSomeoneElse ? T.ink : T.muted} />
        <div>
          <p style={{ fontSize: 11, fontWeight: 700, color: bookForSomeoneElse ? T.ink : T.muted, letterSpacing: ".08em" }}>BOOK FOR SOMEONE ELSE</p>
          <p style={{ fontSize: 10, color: T.muted, marginTop: 2 }}>The stay will be booked under another person's name</p>
        </div>
      </button>

      <AnimatePresence>
        {bookForSomeoneElse && (
          <motion.div initial={{ opacity: 0, y: -8, height: 0 }} animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, y: -8, height: 0 }} transition={{ duration: .2 }}
            style={{ marginTop: 10, padding: 14, background: T.bgSoft, border: `1px solid ${T.border}`, borderRadius: 3, overflow: "hidden" }}>
            <p style={{ fontSize: 10, color: T.muted, marginBottom: 12, letterSpacing: ".1em" }}>GUEST DETAILS — an account will be set up for them</p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
              <Field label="Guest First Name *">
                <input className="fi" placeholder="Their first name" value={recipientFirstName} onChange={e => setRecipientFirstName(e.target.value)} />
              </Field>
              <Field label="Guest Last Name *">
                <input className="fi" placeholder="Their last name" value={recipientLastName} onChange={e => setRecipientLastName(e.target.value)} />
              </Field>
            </div>
            <Field label="Guest Email *">
              <div style={{ position: "relative" }}>
                <input className="fi" placeholder="guest@email.com" value={recipientEmail} onChange={e => setRecipientEmail(e.target.value)} style={{ paddingLeft: 38 }} />
                <Mail size={13} color={T.muted} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
              </div>
            </Field>
            <div style={{ marginTop: 10 }}>
              <Field label="Guest WhatsApp Number *">
                <div style={{ display: "flex" }}>
                  <DialField dialCode={recipientDialCode} onChange={dc => setRecipientDialCode(dc)} />
                  <input className="fi" placeholder="e.g. 7001234567" value={recipientPhone}
                    onChange={e => setRecipientPhone(stripDialCode(e.target.value, recipientDialCode))}
                    style={{ flex: 1, borderRadius: "0 2px 2px 0" }} inputMode="tel" />
                </div>
                <p style={{ fontSize: 10, color: T.muted, marginTop: 4 }}>{recipientDialCode} + their number</p>
              </Field>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  /* ── Price breakdown ── */
  const PriceContent = () => {
  const nights = checkIn && checkOut ? dayjs(checkOut).diff(dayjs(checkIn), "day") : reservedDuration;
  const weeks  = Math.ceil(nights / 7);
  const months = Math.ceil(nights / 30);
  const years  = Math.ceil(nights / 365);

  const rateDisplay = currentListing?.rate
    ? `${symbol}${convertRaw(currentListing.rate, "EUR").toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "";

  const unitLabel = (() => {
    if (isPerHour)  return `${rateDisplay} × ${totalHours} hour${totalHours !== 1 ? "s" : ""}`;
    if (isPerWeek)  return `${rateDisplay} × ${weeks} week${weeks !== 1 ? "s" : ""}`;
    if (isPerMonth) return `${rateDisplay} × ${months} month${months !== 1 ? "s" : ""}`;
    if (isPerYear)  return `${rateDisplay} × ${years} year${years !== 1 ? "s" : ""}`;
    const perPersonSuffix = currentListing?.rateType === "perNightPerPerson" ? ` × ${adults} guest${adults !== 1 ? "s" : ""}` : "";
    return `${rateDisplay} × ${nights} night${nights !== 1 ? "s" : ""}${perPersonSuffix}`;
  })();

  return (
    <div>
      <FeeRow label={unitLabel} value={`${symbol}${convertRaw(baseStayCost, "EUR").toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
      {discountedStayCost < baseStayCost && (
        <FeeRow label="Discount" value={`-${symbol}${convertRaw(baseStayCost - discountedStayCost, "EUR").toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} green />
      )}
      {Object.entries(selectedExtrasMap).map(([k, v]) => (
        <FeeRow key={k} label={k} value={`${symbol}${convertRaw(Number(v), "EUR").toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
      ))}
      <FeeRow label="Service fee" value={`${symbol}${convertRaw(guestsFee, "EUR").toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} muted />
      {creditsUsed > 0 && (
        <FeeRow label="Credits used" value={`-${symbol}${convertRaw(creditsUsed, "EUR").toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} green />
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", paddingTop: 12, marginTop: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: ".15em", color: T.ink }}>TOTAL</span>
        <span style={{ fontSize: 22, fontWeight: 900, background: T.gradH, backgroundSize: "200% 100%", animation: "gradSpin 3s ease infinite", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
          {symbol}{prettyAmount}
        </span>
      </div>
      <p style={{ fontSize: 10, color: T.muted, textAlign: "right", marginTop: 2 }}>All taxes included · Charged in EUR</p>
    </div>
  );
};

  /* ── Payment method + pay button ── */
  const PaymentContent = () => (
    <div>
      <p style={{ fontSize: 10, fontWeight: 700, color: T.muted, letterSpacing: ".14em", textTransform: "uppercase" as const, marginBottom: 10 }}>Choose payment method</p>
      <div className="pay-method-grid" style={{ gridTemplateColumns: isAfrica ? "1fr 1fr" : "1fr" }}>
        {/* Stripe */}
        <div className={`pay-method-card stripe${selectedPayment === "stripe" ? " selected" : ""}`} onClick={() => setSelectedPayment("stripe")}>
          <div className="pay-method-top-bar" style={{ background: "linear-gradient(90deg,#0D0D0D,#2A2018,#0D0D0D)" }} />
          <div className="pay-method-check">{selectedPayment === "stripe" && <Check size={9} color="#fff" />}</div>
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="28" height="28" rx="4" fill="#635BFF"/>
            <path d="M13.007 11.16c0-.78.642-1.08 1.705-1.08 1.525 0 3.452.46 4.976 1.28V7.6C18.17 6.92 16.66 6.6 15.15 6.6c-3.17 0-5.278 1.66-5.278 4.42 0 4.32 5.948 3.62 5.948 5.48 0 .92-.8 1.22-1.926 1.22-1.665 0-3.79-.68-5.474-1.6v3.8c1.865.8 3.75 1.14 5.474 1.14 3.251 0 5.494-1.6 5.494-4.4-.02-4.66-5.98-3.82-5.98-5.5z" fill="#fff"/>
          </svg>
          <span className="pay-method-label">Stripe</span>
          <span className="pay-method-sub">Cards · Apple Pay · Google Pay</span>
        </div>
        {/* M-Pesa */}
        {isAfrica && (
          <div className={`pay-method-card mpesa${selectedPayment === "mpesa" ? " selected" : ""}`} onClick={() => setSelectedPayment("mpesa")}>
            <div className="pay-method-top-bar" style={{ background: "linear-gradient(90deg,#00A651,#007A3D)" }} />
            <div className="pay-method-check">{selectedPayment === "mpesa" && <Check size={9} color="#fff" />}</div>
            <Smartphone size={28} color={selectedPayment === "mpesa" ? "#00A651" : T.muted} />
            <span className="pay-method-label" style={{ color: selectedPayment === "mpesa" ? "#00A651" : undefined }}>M-Pesa</span>
            <span className="pay-method-sub">via Cosmoola · Mobile money</span>
          </div>
        )}
      </div>

      <AnimatePresence mode="wait">
        {selectedPayment === "stripe" ? (
          <motion.button key="stripe-btn"
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: .18 }}
            onClick={handlePayStripe}
            className={`pay-btn-stripe${!paying ? " active" : " inactive"}`}>
            {payingStripe
              ? <span style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,.6)", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
              : <Lock size={13} />}
            {payingStripe ? "Processing…" : `Pay with Stripe  ${symbol}${prettyAmount}`}
          </motion.button>
        ) : (
          <motion.button key="mpesa-btn"
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -4 }} transition={{ duration: .18 }}
            onClick={handlePayMpesa}
            className={`pay-btn-mpesa${!paying ? " active" : " inactive"}`}>
            {payingMpesa
              ? <span style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,.6)", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} />
              : <Smartphone size={13} />}
            {payingMpesa ? "Connecting to M-Pesa…" : `Pay with M-Pesa  ${symbol}${prettyAmount}`}
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {payError && (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            style={{ display: "flex", alignItems: "flex-start", gap: 8, background: "rgba(204,34,0,.05)", border: "1px solid rgba(204,34,0,.2)", padding: "11px 13px", marginTop: 10, borderRadius: 3 }}>
            <AlertCircle size={13} color={T.red} style={{ flexShrink: 0, marginTop: 1 }} />
            <span style={{ fontSize: 11, color: T.red, flex: 1 }}>{payError}</span>
            <button onClick={() => setPayError(null)} style={{ background: "none", border: "none", padding: 0 }}><X size={12} color={T.red} /></button>
          </motion.div>
        )}
      </AnimatePresence>

      <p style={{ fontSize: 11, color: T.muted, textAlign: "center", marginTop: 10 }}>
        You won't be charged until your booking is confirmed
      </p>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 20, marginTop: 12 }}>
        {[{ icon: "🔒", label: "Secure" }, { icon: "✅", label: "Verified" }, { icon: "↩️", label: "Refunds" }].map(b => (
          <div key={b.label} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <span style={{ fontSize: 11 }}>{b.icon}</span>
            <span style={{ fontSize: 9, color: T.muted, fontWeight: 700, letterSpacing: ".08em" }}>{b.label.toUpperCase()}</span>
          </div>
        ))}
      </div>
    </div>
  );

  /* ════════════════════════════════════════════
     MOBILE — expandable accordion cards
  ════════════════════════════════════════════ */
  if (!isDesktop) {
    return (
      <div style={{ minHeight: "100vh", background: T.bg, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        <style>{CSS}</style>

        {/* sticky header */}
        <div style={{ position: "sticky", top: 0, zIndex: 200, background: "#fff", borderBottom: "1px solid #EDE8E0", boxShadow: "0 2px 10px rgba(0,0,0,.06)" }}>
          <div style={{ height: 3, background: T.grad }} />
          <div style={{ display: "flex", alignItems: "center", padding: "0 16px", height: 50, gap: 14 }}>
            <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", color: T.muted, display: "flex", alignItems: "center", gap: 5, fontSize: 11, letterSpacing: ".1em", textTransform: "uppercase" as const }}>
              <ArrowLeft size={14} /> Back
            </button>
            <p style={{ flex: 1, textAlign: "center", fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 17, fontWeight: 700, color: T.ink }}>Confirm Reservation</p>
            <div style={{ width: 52 }} />
          </div>
        </div>

        <div style={{ padding: "16px 14px 120px" }}>

          <ExpandCard title="Your stay" defaultOpen={true}>
            <ListingSummaryContent />
            <div style={{ marginTop: 14 }}><StaySummaryContent /></div>
          </ExpandCard>

          <ExpandCard title="Contact details" icon={<Mail size={13} />} defaultOpen={!!user}>
            <ContactContent />
          </ExpandCard>

          <ExpandCard title="Price breakdown" icon={<CreditCard size={13} />} defaultOpen={false}>
            <PriceContent />
          </ExpandCard>

          <ExpandCard title="Payment" icon={<ShieldCheck size={13} />} defaultOpen={true}>
            <PaymentContent />
          </ExpandCard>

        </div>

        {/* mobile sticky pay bar */}
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, background: "rgba(255,255,255,.97)", borderTop: "1px solid #EDE8E0", padding: "12px 16px", zIndex: 200, backdropFilter: "blur(10px)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <span style={{ fontSize: 11, color: T.muted }}>Total</span>
            <span style={{ fontSize: 20, fontWeight: 900, color: T.ink }}>{symbol}{prettyAmount}</span>
          </div>
          <AnimatePresence mode="wait">
            {selectedPayment === "stripe" ? (
              <motion.button key="s" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: .15 }}
                onClick={handlePayStripe} className={`pay-btn-stripe${!paying ? " active" : " inactive"}`}>
                {payingStripe ? <span style={{ width: 12, height: 12, border: "2px solid rgba(255,255,255,.6)", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} /> : <Lock size={12} />}
                {payingStripe ? "Processing…" : `Stripe  ${symbol}${prettyAmount}`}
              </motion.button>
            ) : (
              <motion.button key="m" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: .15 }}
                onClick={handlePayMpesa} className={`pay-btn-mpesa${!paying ? " active" : " inactive"}`}>
                {payingMpesa ? <span style={{ width: 12, height: 12, border: "2px solid rgba(255,255,255,.6)", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 1s linear infinite" }} /> : <Smartphone size={12} />}
                {payingMpesa ? "Connecting…" : `M-Pesa  ${symbol}${prettyAmount}`}
              </motion.button>
            )}
          </AnimatePresence>
        </div>
      </div>
    );
  }

  /* ════════════════════════════════════════════
     DESKTOP — full cards, two-column
  ════════════════════════════════════════════ */
  return (
    <div style={{ minHeight: "100vh", background: "#F4F1EC", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <style>{CSS}</style>

      {/* header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #EDE8E0", boxShadow: "0 1px 8px rgba(0,0,0,.05)" }}>
        <div style={{ height: 3, background: T.grad }} />
        <div style={{ maxWidth: 1080, margin: "0 auto", display: "flex", alignItems: "center", padding: "0 40px", height: 56 }}>
          <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", color: T.muted, display: "flex", alignItems: "center", gap: 6, fontSize: 11, letterSpacing: ".1em", textTransform: "uppercase" as const }}>
            <ArrowLeft size={14} /> Back
          </button>
          <p style={{ flex: 1, textAlign: "center", fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 22, fontWeight: 700, color: T.ink, letterSpacing: ".02em" }}>
            Confirm your reservation
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            {[{ icon: <ShieldCheck size={13} color={T.gold} />, l: "Encrypted" }, { icon: <CreditCard size={13} color={T.gold} />, l: "Stripe" }, { icon: <RotateCcw size={13} color={T.gold} />, l: "Refunds" }].map(b => (
              <div key={b.l} style={{ display: "flex", alignItems: "center", gap: 5 }}>
                {b.icon}<span style={{ fontSize: 10, color: T.muted, fontWeight: 700, letterSpacing: ".08em" }}>{b.l.toUpperCase()}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "32px 40px 60px", display: "grid", gridTemplateColumns: "minmax(0,1fr) 360px", gap: 28, alignItems: "start" }}>

        {/* LEFT */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Listing + stay */}
          <div style={{ background: "#fff", border: "1px solid #EDE8E0", borderRadius: 10, overflow: "hidden", boxShadow: "0 2px 14px rgba(0,0,0,.05)", animation: "slideUp .3s ease" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid #EDE8E0" }}>
              <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".16em", color: T.muted, marginBottom: 12 }}>YOUR STAY</p>
              <ListingSummaryContent />
            </div>
            <div style={{ padding: "16px 20px" }}>
              <StaySummaryContent />
            </div>
          </div>

          {/* Contact */}
          <div style={{ background: "#fff", border: "1px solid #EDE8E0", borderRadius: 10, overflow: "hidden", boxShadow: "0 2px 14px rgba(0,0,0,.05)", animation: "slideUp .35s ease" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #EDE8E0", display: "flex", alignItems: "center", gap: 10 }}>
              <Mail size={14} color={T.gold} />
              <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".16em", color: T.ink }}>CONTACT DETAILS</p>
              {user && <span style={{ marginLeft: "auto", fontSize: 10, color: T.green, fontWeight: 700, background: "rgba(21,128,61,.07)", padding: "2px 8px", borderRadius: 3 }}>● Pre-filled</span>}
            </div>
            <div style={{ padding: "16px 20px" }}>
              <ContactContent />
            </div>
          </div>

        </div>

        {/* RIGHT */}
        <div style={{ position: "sticky", top: 88, display: "flex", flexDirection: "column", gap: 14 }}>

          {/* Price */}
          <div style={{ background: "#fff", border: "1px solid #EDE8E0", borderRadius: 10, overflow: "hidden", boxShadow: "0 2px 14px rgba(0,0,0,.05)", animation: "slideUp .28s ease" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #EDE8E0", display: "flex", alignItems: "center", gap: 10 }}>
              <CreditCard size={14} color={T.gold} />
              <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".16em", color: T.ink }}>PRICE BREAKDOWN</p>
            </div>
            <div style={{ padding: "14px 20px" }}>
              <PriceContent />
            </div>
          </div>

          {/* Payment */}
          <div style={{ background: "#fff", border: "1px solid #EDE8E0", borderRadius: 10, overflow: "hidden", boxShadow: "0 2px 14px rgba(0,0,0,.05)", animation: "slideUp .32s ease" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #EDE8E0", display: "flex", alignItems: "center", gap: 10 }}>
              <ShieldCheck size={14} color={T.gold} />
              <p style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".16em", color: T.ink }}>PAYMENT</p>
            </div>
            <div style={{ padding: "16px 20px" }}>
              <PaymentContent />
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}